import { useEffect, useState } from "react";
import { Trash2, Database, ScrollText, History, Loader2, ShieldAlert } from "lucide-react";
import { api } from "../../lib/api";
import BulkDeletePanel from "./BulkDeletePanel";

/**
 * V28 — Bulk Delete Management top-level container.
 *  - Loads available modules from /admin/bulk-delete/modules (super-admin gated)
 *  - Renders a horizontal tab strip + the reusable BulkDeletePanel per module
 *  - Also exposes an "Audit" tab listing recent bulk delete events
 */
export default function BulkDeleteSection() {
    const [modules, setModules] = useState(null);
    const [activeKey, setActiveKey] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await api.get("/admin/bulk-delete/modules");
                if (cancelled) return;
                setModules(res.data.modules || []);
                if (res.data.modules?.length) setActiveKey(res.data.modules[0].key);
            } catch (e) {
                if (cancelled) return;
                const status = e.response?.status;
                setErr(status === 403
                    ? "Bulk Delete requires Super Mode. Switch to Super Mode to continue."
                    : (e.response?.data?.detail || e.message || "Failed to load modules"));
            }
        })();
        return () => { cancelled = true; };
    }, []);

    if (err) {
        return (
            <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 text-amber-900 text-sm inline-flex items-start gap-2" data-testid="bulk-delete-locked">
                <ShieldAlert className="w-4 h-4 mt-0.5" />
                <span>{err}</span>
            </div>
        );
    }
    if (!modules) {
        return <div className="text-slate-500 text-sm inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading modules…</div>;
    }

    const active = modules.find((m) => m.key === activeKey) || modules[0];

    return (
        <div className="space-y-4" data-testid="bulk-delete-section">
            <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-red-50 border border-red-200">
                    <Trash2 className="w-5 h-5 text-red-700" />
                </div>
                <div className="flex-1">
                    <h2 className="text-lg font-bold text-slate-900">Bulk Delete Management</h2>
                    <p className="text-xs text-slate-500">Permanently remove unwanted records to free up storage. All deletes are audited and irreversible — proceed with care.</p>
                </div>
            </div>

            {/* Module tab strip */}
            <div className="flex items-center gap-1 flex-wrap border-b border-slate-200">
                {modules.map((m) => (
                    <button
                        key={m.key}
                        onClick={() => setActiveKey(m.key)}
                        className={`px-3 py-2 text-sm font-semibold border-b-2 transition ${
                            activeKey === m.key
                                ? "border-[#1E3A8A] text-[#1E3A8A]"
                                : "border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300"
                        }`}
                        data-testid={`bulk-delete-tab-${m.key}`}
                    >
                        <Database className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" /> {m.label}
                    </button>
                ))}
                <button
                    onClick={() => setActiveKey("__audit__")}
                    className={`px-3 py-2 text-sm font-semibold border-b-2 transition ml-auto ${
                        activeKey === "__audit__"
                            ? "border-[#1E3A8A] text-[#1E3A8A]"
                            : "border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300"
                    }`}
                    data-testid="bulk-delete-tab-audit"
                >
                    <History className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" /> Audit Log
                </button>
            </div>

            {activeKey === "__audit__"
                ? <BulkDeleteAuditPanel />
                : <BulkDeletePanel moduleKey={active.key} meta={active} />}
        </div>
    );
}

function BulkDeleteAuditPanel() {
    const [rows, setRows] = useState(null);
    useEffect(() => {
        api.get("/admin/bulk-delete/audit?limit=100")
            .then((r) => setRows(r.data.items || []))
            .catch(() => setRows([]));
    }, []);
    if (rows === null) return <div className="text-slate-500 text-sm inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading audit…</div>;
    if (!rows.length) return <div className="bg-white border border-slate-200 rounded-xl p-6 text-center text-sm text-slate-500"><ScrollText className="w-8 h-8 mx-auto text-slate-300 mb-2" />No bulk delete events recorded yet.</div>;
    return (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="bulk-delete-audit-list">
            <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
                <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-slate-50 border-b border-slate-200 z-[1]">
                        <tr>
                            {["When", "Admin", "Module", "Event", "Records", "IP"].map((h) => (
                                <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-slate-600">{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={i} className="border-b border-slate-100">
                                <td className="px-3 py-2 text-xs whitespace-nowrap">{new Date(r.at).toLocaleString()}</td>
                                <td className="px-3 py-2 text-xs">{r.admin_email || "—"}</td>
                                <td className="px-3 py-2 text-xs">{r.module_label || r.module}</td>
                                <td className="px-3 py-2 text-xs">
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                                        r.event === "bulk_delete_by_ids" ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-800"
                                    }`}>{r.event.replace("bulk_delete_", "").replaceAll("_", " ")}</span>
                                </td>
                                <td className="px-3 py-2 text-xs font-bold text-red-700">{r.count}</td>
                                <td className="px-3 py-2 text-xs text-slate-500 font-mono">{r.ip || "—"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
